import { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { contentService } from '../services/api';
import { formatDate, getAvatarUrl } from './utils/Common';
import { getPriorityColor, getPriorityIcon } from './utils/Home';

/* ── Lightweight markdown renderer ────────────────────────
   Supports: # ## ### headings, **bold**, *italic*, - lists
   ─────────────────────────────────────────────────────── */
const inlineFormat = (text, key) => {
  const parts = [];
  let rest = text;
  let i = 0;
  const RE = /(\*\*(.+?)\*\*|\*(.+?)\*)/g;
  let m;
  let last = 0;
  RE.lastIndex = 0;
  while ((m = RE.exec(rest)) !== null) {
    if (m.index > last) parts.push(rest.slice(last, m.index));
    if (m[2] !== undefined) parts.push(<strong key={i++}>{m[2]}</strong>);
    else if (m[3] !== undefined) parts.push(<em key={i++}>{m[3]}</em>);
    last = m.index + m[0].length;
  }
  if (last < rest.length) parts.push(rest.slice(last));
  return <span key={key}>{parts}</span>;
};

const renderContent = (text) => {
  if (!text) return null;
  const lines = text.split('\n');
  const elements = [];
  let listItems = [];
  let key = 0;

  const flushList = () => {
    if (listItems.length) {
      elements.push(<ul key={key++} className="article-list">{listItems}</ul>);
      listItems = [];
    }
  };

  for (const line of lines) {
    const h3 = line.match(/^###\s+(.+)/);
    const h2 = line.match(/^##\s+(.+)/);
    const h1 = line.match(/^#\s+(.+)/);
    const li = line.match(/^[-*]\s+(.+)/);

    if (h1) { flushList(); elements.push(<h2 key={key++} className="article-h1">{inlineFormat(h1[1], key++)}</h2>); }
    else if (h2) { flushList(); elements.push(<h3 key={key++} className="article-h2">{inlineFormat(h2[1], key++)}</h3>); }
    else if (h3) { flushList(); elements.push(<h4 key={key++} className="article-h3">{inlineFormat(h3[1], key++)}</h4>); }
    else if (li) { listItems.push(<li key={key++}>{inlineFormat(li[1], key++)}</li>); }
    else if (line.trim() === '') { flushList(); elements.push(<div key={key++} className="article-spacer" />); }
    else { flushList(); elements.push(<p key={key++} className="article-p">{inlineFormat(line, key++)}</p>); }
  }
  flushList();
  return elements;
};

const PRIORITY_META = {
  urgent: { label: 'Pilne',   bg: '#fef2f2', border: '#fca5a5' },
  high:   { label: 'Ważne',  bg: '#fffbeb', border: '#fcd34d' },
  normal: { label: 'Info',   bg: '#eff6ff', border: '#bfdbfe' },
  low:    { label: 'Niski',  bg: '#f9fafb', border: '#e5e7eb' },
};

const AnnouncementCard = ({ a }) => {
  const color  = getPriorityColor(a.priority);
  const icon   = getPriorityIcon(a.priority);
  const meta   = PRIORITY_META[a.priority] || PRIORITY_META.normal;
  const initials = (a.author_name || '?').charAt(0).toUpperCase();
  return (
    <div
      className={`announcement-card priority-${a.priority}${a.is_pinned ? ' is-pinned' : ''}`}
      style={{ borderLeftColor: color, background: meta.bg }}
    >
      <div className="announcement-header">
        <span className="priority-icon">{icon}</span>
        <span className="announcement-title">{a.title}</span>
        <div className="announcement-badges">
          {a.is_pinned && <span className="pinned-badge">📌 Przypięte</span>}
          <span className="priority-badge" style={{ background: color }}>
            {meta.label}
          </span>
        </div>
      </div>
      <div className="announcement-content article-body">{renderContent(a.content)}</div>
      <div className="announcement-footer">
        <span className="announcement-author">
          <span className="author-avatar">{initials}</span>
          {a.author_name}
        </span>
        <time className="announcement-date">{formatDate(a.created_at)}</time>
      </div>
    </div>
  );
};

const Home = () => {
  const { user } = useAuth();
  const [homeData, setHomeData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchHomeData();
  }, []);

  const fetchHomeData = async () => {
    try {
      setLoading(true);
      const data = await contentService.getHomePageData();
      setHomeData(data);
    } catch (err) {
      console.error('Error fetching home data:', err);
      setError('Failed to load content');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="page-container home-page">
      <div className="page-header home-header-with-avatar">
        <div className="home-avatar">
          {getAvatarUrl(user?.avatar) ? (
            <img src={getAvatarUrl(user?.avatar)} alt="Profile" className="home-avatar-image" />
          ) : (
            <div className="home-avatar-placeholder">
              {user?.full_name?.charAt(0)?.toUpperCase() || 'U'}
            </div>
          )}
        </div>
        <div className="home-header-text">
          <h2>Witaj, {user?.full_name}!</h2>
          <p>Portal pracowniczy • Strona główna</p>
        </div>
      </div>

      {loading ? (
        <div className="loading-spinner">Ładowanie treści...</div>
      ) : error ? (
        <div className="error-message">{error}</div>
      ) : (
        <div className="home-grid">

          {/* ── News ── */}
          <div className="home-section news-section">
            <div className="section-header">
              <span className="section-header__icon">📰</span>
              <h3>Aktualności</h3>
              {homeData?.news?.length > 0 && (
                <span className="section-header__count">{homeData.news.length}</span>
              )}
            </div>
            {homeData?.news?.length > 0 ? (
              <div className="news-list">
                {homeData.news.map(article => (
                  <div key={article.id} className="news-card">
                    {article.image_url && (
                      <div className="news-image">
                        <img src={article.image_url} alt={article.title} />
                      </div>
                    )}
                    <div className="news-content">
                      <div className="news-title-row">
                        {article.is_pinned && <span className="pinned-icon">📌</span>}
                        <h4 className="news-title">{article.title}</h4>
                      </div>
                      <div className="news-summary article-body">
                        {renderContent(article.summary || article.content)}
                      </div>
                      <div className="news-footer">
                        <span className="news-author">
                          <span className="author-avatar">
                            {(article.author_name || '?').charAt(0).toUpperCase()}
                          </span>
                          {article.author_name}
                        </span>
                        <time className="news-date">{formatDate(article.created_at)}</time>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="no-content-box">
                <span>📭</span>
                <p>Brak dostępnych artykułów</p>
              </div>
            )}
          </div>

          {/* ── Announcements ── */}
          <div className="home-section announcements-section">
            <div className="section-header">
              <span className="section-header__icon">📢</span>
              <h3>Ogłoszenia</h3>
              {((homeData?.global_announcements?.length || 0) + (homeData?.department_announcements?.length || 0)) > 0 && (
                <span className="section-header__count">
                  {(homeData?.global_announcements?.length || 0) + (homeData?.department_announcements?.length || 0)}
                </span>
              )}
            </div>

            {/* Global */}
            <div className="announcements-subsection">
              <div className="subsection-title">
                <span>🌐</span> Ogłoszenia ogólnofirmowe
              </div>
              {homeData?.global_announcements?.length > 0 ? (
                <div className="announcements-list">
                  {homeData.global_announcements.map(a => (
                    <AnnouncementCard key={a.id} a={a} />
                  ))}
                </div>
              ) : (
                <p className="no-content">Brak ogłoszeń ogólnofirmowych</p>
              )}
            </div>

            {/* Department */}
            {user?.department && (
              <div className="announcements-subsection">
                <div className="subsection-title">
                  <span>🏢</span> Dział: {user.department}
                </div>
                {homeData?.department_announcements?.length > 0 ? (
                  <div className="announcements-list">
                    {homeData.department_announcements.map(a => (
                      <AnnouncementCard key={a.id} a={a} />
                    ))}
                  </div>
                ) : (
                  <p className="no-content">Brak ogłoszeń w dziale</p>
                )}
              </div>
            )}
          </div>

        </div>
      )}
    </div>
  );
};

export default Home;
